import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { asset } from "@/lib/assets";
const signupHL = asset("case-studies/homelight-offers/SignupHL.jpg");
const previousLanding = asset("case-studies/homelight-offers/previouslanding.jpg");
const newLanding = asset("case-studies/homelight-offers/newlanding.jpg");
const getCashOffer = asset("case-studies/homelight-offers/getcashoffer.mp4");

const META = [
  { label: "Role", value: "Product builder (design-focused)" },
  { label: "Scope", value: "UX · UI · Product Strategy · Frontend Implementation" },
  { label: "Tools", value: "Figma · Figma Make · Cursor · GitHub" },
  { label: "Status", value: "Ongoing project" },
];

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "team", label: "Team & Workflow" },
  { id: "shipped", label: "What I Shipped" },
  { id: "whats-next", label: "What's Next" },
  { id: "learned", label: "What I Learned" },
];

function ImageSlot({
  src,
  alt,
  className = "",
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
        onClick={() => setLightbox(true)}
        title="Click to expand"
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = "none";
            const parent = el.parentElement;
            if (parent && !parent.querySelector(".placeholder-label")) {
              const label = document.createElement("p");
              label.className =
                "placeholder-label text-xs text-[#888888] text-center p-4";
              label.textContent = alt;
              parent.appendChild(label);
            }
          }}
        />
      </div>
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(false)}
        >
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
            aria-label="Close"
          >×</button>
        </div>,
        document.body
      )}
    </>
  );
}

function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  className = "",
}: {
  beforeSrc: string;
  afterSrc: string;
  className?: string;
}) {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hinted = useRef(false);

  // Hint animation: gentle oscillation once on mount
  useEffect(() => {
    if (hinted.current) return;
    hinted.current = true;
    const timeout = setTimeout(() => {
      const startTime = performance.now();
      const duration = 1400;
      let frame: number;
      const animate = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        setPosition(50 + Math.sin(t * Math.PI * 2) * 18);
        if (t < 1) frame = requestAnimationFrame(animate);
        else setPosition(50);
      };
      frame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(frame);
    }, 900);
    return () => clearTimeout(timeout);
  }, []);

  const getPos = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 50;
    return Math.max(2, Math.min(98, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e: MouseEvent) => setPosition(getPos(e.clientX));
    const up = () => setIsDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [isDragging, getPos]);

  useEffect(() => {
    if (!isDragging) return;
    const move = (e: TouchEvent) => {
      e.preventDefault(); // prevent page scroll while dragging handle
      if (e.touches[0]) setPosition(getPos(e.touches[0].clientX));
    };
    const end = () => setIsDragging(false);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [isDragging, getPos]);

  return (
    /* Outer wrapper: position:relative so the handle can be pinned to the visible centre */
    <div className={`relative rounded-2xl ${className}`}>
      {/* Scroll container */}
      <div
        ref={containerRef}
        className="select-none rounded-2xl border border-[#E2E8F0] bg-[#EEF2FF] overflow-x-hidden overflow-y-auto"
        style={{ cursor: isDragging ? "col-resize" : "default", maxHeight: "70vh" }}
        onMouseDown={onMouseDown}
        onTouchStart={(e) => { e.stopPropagation(); setIsDragging(true); }}
      >
        {/* Sticky labels */}
        <div className="sticky top-0 z-30 flex justify-between px-3 pt-3 pb-2 pointer-events-none">
          <span className="px-2 py-1 bg-[#1A1A1A]/70 backdrop-blur-sm rounded-md text-white text-xs font-medium">Before</span>
          <span className="px-2 py-1 bg-[#1A1A1A]/70 backdrop-blur-sm rounded-md text-white text-xs font-medium">After</span>
        </div>

        {/* Image stack — natural height, fully scrollable */}
        <div className="-mt-8" style={{ display: "grid" }}>
          <img
            src={afterSrc}
            alt="After"
            className="w-full h-auto block"
            style={{ gridArea: "1/1" }}
            draggable={false}
          />
          <div
            className="w-full"
            style={{ gridArea: "1/1", clipPath: `inset(0 ${100 - position}% 0 0)` }}
          >
            <img
              src={beforeSrc}
              alt="Before"
              className="w-full h-auto block"
              draggable={false}
            />
          </div>
          {/* Divider line — spans full content height */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-md pointer-events-none"
            style={{ left: `${position}%`, transform: "translateX(-50%)", zIndex: 10 }}
          />
        </div>
      </div>

      {/* Handle — outside scroll container so it never scrolls away; always at visual centre */}
      <div
        className="absolute top-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center"
        style={{ left: `${position}%`, transform: "translate(-50%, -50%)", cursor: "col-resize", touchAction: "none" }}
        onMouseDown={onMouseDown}
        onTouchStart={(e) => { e.stopPropagation(); setIsDragging(true); }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M6 3L1 9L6 15" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 3L17 9L12 15" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

function VideoSlot({ src, className = "" }: { src: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { video.play().catch(() => {}); }
        else { video.pause(); }
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return (
    <div className={`overflow-hidden bg-[#1A1A1A] rounded-2xl ${className}`}>
      <video ref={videoRef} src={src} loop muted playsInline controls className="w-full h-auto object-cover" />
    </div>
  );
}

function InsightCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 bg-[#FDF3E7] border border-[#F5C542]/20 rounded-2xl p-5">
      <span className="text-xl shrink-0 mt-0.5">💡</span>
      <p className="text-[#7A4A1A] text-[0.9375rem] leading-[1.75]">{children}</p>
    </div>
  );
}

function SidebarNav({ activeSection, visible }: { activeSection: string; visible: boolean }) {
  return (
    <nav
      className={`hidden xl:block fixed left-8 top-1/2 -translate-y-1/2 z-40 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="relative flex flex-col">
        <div className="absolute left-[5px] top-3 bottom-3 w-px bg-[#E2E8F0]" />
        {NAV_SECTIONS.map((s) => {
          const isActive = activeSection === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex items-center gap-3 py-[7px] group"
            >
              <div
                className={`w-[11px] h-[11px] rounded-full border-2 shrink-0 relative z-10 transition-all duration-200 ${
                  isActive
                    ? "border-[#F5C542] bg-[#F5C542] scale-110"
                    : "border-[#D4CFC9] bg-[#F7F8FA] group-hover:border-[#F5C542]/60"
                }`}
              />
              <span
                className={`text-xs transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "text-[#1A1A1A] font-semibold"
                    : "text-[#AAAAAA] group-hover:text-[#555]"
                }`}
              >
                {s.label}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}

export function CaseStudyHomelightOffers() {
  const [activeSection, setActiveSection] = useState("overview");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const hero = heroRef.current;
      if (!hero) return;
      setSidebarVisible(hero.getBoundingClientRect().bottom <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 }
    );

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      <SidebarNav activeSection={activeSection} visible={sidebarVisible} />

      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div ref={heroRef} className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
            HomeLight Offers (beta) · Ongoing project
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            Shipping Products{" "}
            <span className="text-[#1D3FE8]">in the AI Era.</span>
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            I take small initiatives from idea to production and ship them.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[#E2E8F0]">
            {META.map((m) => (
              <div key={m.label}>
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  {m.label}
                </p>
                <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-snug">
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>

      {/* Metrics banner — hidden until stronger metrics are available */}

      {/* Sections */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12">

        {/* Overview */}
        <div id="overview">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Overview
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
              About the product.
            </h2>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-6">
              HomeLight Offers enables real estate agents to request cash offers
              from investors for their clients' properties. Agents submit a
              property and receive multiple offers within 48 hours. If accepted,
              HomeLight connects the agent and investor to move forward. The goal
              of the beta is to test the model and validate demand.
            </p>
          </AnimatedSection>
        </div>

        {/* Team & Workflow */}
        <div id="team">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Team & Workflow
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
              Not just what we built —{" "}
              <span className="text-[#1D3FE8]">how we build it.</span>
            </h2>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
              The team is small, the process is lean, and AI tools are part of
              the daily workflow. Anyone can propose and ship improvements.
            </p>

            <div className="grid sm:grid-cols-[1.2fr_1fr] gap-16 items-start">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-4">
                  My workflow in practice
                </p>
                <div className="flex flex-col gap-0">
                  {[
                    "Audit the experience",
                    "Design a solution",
                    "Implement the frontend",
                    "Open a PR and ship",
                  ].map((step, i) => (
                    <div
                      key={i}
                      className="flex gap-4 items-start py-4 border-b border-[#E2E8F0] last:border-b-0"
                    >
                      <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm w-6 shrink-0 mt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-[#1A1A1A] font-medium text-[0.9375rem]">
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-6 self-start">
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#F5C542] mb-4">
                  The team
                </p>
                {["Eng", "Product", "Design"].map((role) => (
                  <div
                    key={role}
                    className="flex gap-3 items-center py-3 border-b border-white/[0.08] last:border-b-0"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#F5C542] shrink-0" />
                    <p className="text-white/70 text-sm">{role}</p>
                  </div>
                ))}
                <p className="text-white/30 text-xs mt-4 leading-relaxed">
                  AI-native tooling makes this process significantly faster than
                  traditional environments.
                </p>
              </div>
            </div>
          <div className="mt-8">
            <InsightCallout>
              My contribution is simple: I identify opportunities, improve the
              experience, and ship them.
            </InsightCallout>
          </div>
          </AnimatedSection>
        </div>

        {/* What I Shipped */}
        <div id="shipped">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              What I Shipped
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-12">
              Three initiatives. All in production.
            </h2>

            {/* Initiative 1 */}
            <div className="mb-16">
              <div className="flex gap-4 items-baseline mb-4">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  01
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Landing page redesign
                </h3>
              </div>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-5 ml-10">
                The original page proved the concept but lacked visual quality and
                clarity. In three days I audited the page, redesigned it in Figma,
                explored variations with Figma Make, implemented the UI using
                Figma MCP and Cursor, added motion and interaction details, and
                shipped to production.
              </p>
              <div className="flex gap-3 items-baseline max-w-[680px] ml-10 mb-6">
                <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
                <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                  <strong className="font-semibold">
                    Built and shipped section by section instead of waiting for a
                    full redesign.
                  </strong>
                </p>
              </div>
              <BeforeAfterSlider
                beforeSrc={previousLanding}
                afterSrc={newLanding}
                className="w-full ml-0"
              />
              <a
                href="https://offers.homelight.com/?_gl=1*10a8hm6*_gcl_au*NzkzNzM3MjcwLjE3NzM3OTYxNDA."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-[0.9375rem] font-medium text-[#1D3FE8] hover:underline"
              >
                Visit the website
                <span aria-hidden="true">↗</span>
              </a>
            </div>

            {/* Initiative 2 */}
            <div className="mb-16">
              <div className="flex gap-4 items-baseline mb-4">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  02
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Sign up page UI improvements
                </h3>
              </div>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4 ml-10">
                The flow worked, but it didn't match our branding and didn't
                clearly communicate the product value. I brought in the designer
                I manage to explore solutions, then I implemented the frontend
                directly in the codebase.
              </p>
              <ImageSlot
                src={signupHL}
                alt="Sign up page redesign"
                className="w-full aspect-video mt-6 mb-4 ml-10"
              />
              <div className="flex gap-3 items-baseline max-w-[680px] ml-10">
                <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
                <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                  <strong className="font-semibold">
                    No major changes. Just improve the UI and visual design to be consistent with the brand.
                  </strong>
                </p>
              </div>
              <a
                href="https://offers.homelight.com/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 ml-10 text-[0.9375rem] font-medium text-[#1D3FE8] hover:underline"
              >
                Visit sign up page
                <span aria-hidden="true">↗</span>
              </a>
            </div>

            {/* Initiative 3 */}
            <div>
              <div className="flex gap-4 items-baseline mb-4">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  03
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Agent portal integration
                </h3>
              </div>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4 ml-10">
                The most impactful work was connecting the new Offers product to
                the existing agent portal (legacy product). We added a "Get a cash
                offer" action directly in the leads table.
              </p>
              <ul className="flex flex-col gap-2 mb-5 ml-10">
                {[
                  "A logged-in session is created",
                  "Property data is pre-filled",
                  "Agents can submit instantly",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[#444] text-[0.9375rem] leading-[1.7]"
                  >
                    <span className="text-[#1D3FE8] shrink-0">—</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3 items-baseline max-w-[680px] ml-10 mb-5">
                <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
                <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                  <strong className="font-semibold">
                    I led the design of the flow and implementation was divided
                    between the AI builders of the team.
                  </strong>
                </p>
              </div>
              <div className="ml-10">
                <InsightCallout>
                  In the first week after launching the integration we got{" "}
                  <strong>95 subscriptions</strong>, and{" "}
                  <strong>5 property submissions</strong>.
                </InsightCallout>
              </div>
              <div className="ml-10 mt-8">
                <VideoSlot src={getCashOffer} className="w-full rounded-2xl" />
                <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">Walkthrough of the experience</p>
              </div>
            </div>
          </AnimatedSection>
        </div>


        {/* What's Next */}
        <div id="whats-next">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
              What's next
            </p>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4">
              After doing an audit on the Offers portal my backlog looks like this:
            </p>
            <div className="flex flex-col gap-3 max-w-[680px]">
              {[
                "Improve usability of the Property submission wizard",
                "Fix dashboard layout and spacing",
                "Improve the UI of messaging feature",
              ].map((item) => (
                <div key={item} className="flex gap-3 items-baseline">
                  <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
                  <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>

        {/* What I Learned */}
        <div id="learned">
          <AnimatedSection className="py-20">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              What I Learned
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-10">
              Working this way has been an eye opener.
            </h2>

            <div className="flex flex-col gap-6">
              {[
                {
                  label: "Ownership looks different now",
                  body: "The biggest shift apart from the tools is the level of ownership. Design is no longer just defining the experience, it's also shaping how fast it gets built and shipped.",
                },
                {
                  label: "Speed still needs measurement",
                  body: "I see room for improvement on tracking the impact of all these changes. It's not only about shipping things fast, but ensuring you are improving the user experience and moving the metrics for the business.",
                },
                {
                  label: "Engineers made this possible",
                  body: "I wouldn't accomplish any of this without the support, patience and help from engineers. From setting up environments, to explaining the basics of commits, merge, branches, and a hundred more things. My respect to all their dedication and knowledge.",
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] mb-2">
                      {item.label}
                    </p>
                    <p className="text-[#555] text-[0.9375rem] leading-[1.85]">
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>

        {/* Footer nav */}
        <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All case studies
          </Link>
          <div className="text-right">
            <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
              Next case study
            </p>
            <Link
              to="/case-study/design-system"
              className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
            >
              Design System →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
